import React, { useState } from 'react';
import { ReactSearchAutocomplete } from 'react-search-autocomplete';
import { FoodItem, table } from './auto-gen/table';

export type PickedFood = {
  item: FoodItem;
  amt: number;
};

function FoodPrompt({ onSelect }: { onSelect: (food: PickedFood) => void }) {
  const items = table.map((e, i) => ({ id: i, name: e.name, item: e }));
  const [selectedFood, setSelectedFood] = useState<FoodItem|null>(null);
  const [amt, setAmt] = useState<number|null>(null);
  const [searchKey, setSearchKey] = useState(0);

  const submit = () => {
    if (selectedFood !== null && amt !== null && amt > 0.0) {
      const picked = { item: selectedFood, amt: amt };
      onSelect(picked);
      setSelectedFood(null);
      setAmt(null);
      // Remount the search box so its internal text is cleared
      // and focus returns to it for the next ingredient.
      setSearchKey(k => k + 1);
    }
  };
  return (
    <div className="FoodPrompt">
      <span>Escolha um ingrediente</span>
      <ReactSearchAutocomplete
        key={searchKey}
        items={items}
        autoFocus={true}
        onSelect={i => setSelectedFood(i.item)}
        fuseOptions={{ threshold: 0.5 }}
      />
      {selectedFood !== null
        ? (<div className="FoodPrompt-amount">
            <span>Escolha a quantidade (em gramas)</span>
            <div>
              <input
                type='number'
                autoFocus={true}
                onKeyDown={e => {if (e.key === "Enter") submit()}}
                onChange={v => setAmt(+v.target.value)}
              />
              <button onClick={submit}>
                Adicionar
              </button>
            </div>
          </div>)
        : null
      }
    </div>
  )
}

function FoodTable({ pickedFoods }: { pickedFoods: PickedFood[] }) {
  type FoodItemNumbers = Omit<FoodItem, 'name'>;

  const picked = pickedFoods.reduce((acc, i) => {
    (Object.keys(i.item).filter(x => x !== 'name') as (keyof FoodItemNumbers)[]).forEach(k => {

      const base = acc[k] ?? 0.0;

      // t -- totalManufactured
      // x -- 100

      // (100 * t) / totalManufactured

      const nutritional_value_in_recipie = (i.item[k] as number / 100) * i.amt;

      acc[k] = base + nutritional_value_in_recipie;

    }, acc);

    return acc;
  }, {} as Partial<FoodItemNumbers>);

  const totalWeight = pickedFoods.reduce((t, i) => t + i.amt, 0.0);

  const main: (keyof FoodItemNumbers)[] = [
    'energia[kcal]',
    'energia[kj]',
    'lípidos[g]',
    'ácidos gordos saturados[g]',
    'hidratos de carbono [g]',
    'açúcares [g]',
    'fibra  [g]',
    'proteínas [g]',
    'sal  [g]',
    'álcool [g]',
    'água [g]',
  ];

  const componentCal: Record<string, number> = {
    'lípidos[g]': 9,
    'hidratos de carbono [g]': 4,
    'proteínas [g]': 4,
    'fibra  [g]': 2,
    'álcool [g]': 7,
    'sal  [g]': 0,
  };

  const [portionSize, setPortionSize] = useState(100);

  function NutrValue({ name }: { name: keyof FoodItemNumbers }) {
    const f = (v: number) => v.toFixed(2);
    if (name === "energia[kcal]") {
      const calories = picked[name] ?? 0;
      const calculatedCalories = Object.keys(componentCal)
         .reduce(
           (acc, i) => acc + (((picked as Record<string, number>)[i] ?? 0) * componentCal[i]),
           0
         );
      const caloriesP100g = (calories / totalWeight) * portionSize;
      const calculatedCaloriesP100g = (calculatedCalories / totalWeight) * portionSize;
      const diffClass = calculatedCalories < calories ? "calories-diff-below" : "calories-diff-above";
      return (
        <tr className="NutrValue">
          <td>{name}</td>
          <td>{f(calories)}<br/><span></span></td>
          <td>{f(caloriesP100g)}<br/><span></span></td>
          <td>
              {f(calculatedCalories)}<br/>
              <span className={diffClass}>{f(calculatedCalories - calories)}</span>
          </td>
          <td>
              {f(calculatedCaloriesP100g)}<br/>
              <span className={diffClass}>{f(calculatedCaloriesP100g - caloriesP100g)}</span>
          </td>
        </tr>
      );
    } else {
      const weight = picked[name] ?? 0;
      const cCal = componentCal[name];
      const p100 = (weight / totalWeight) * portionSize;
      return (
        <tr className="NutrValue">
          <td>{name}</td>
          <td>{f(weight)}</td>
          <td>{f(p100)}</td>
          <td>{cCal !== undefined ? f(weight * cCal) : ""}</td>
          <td>{cCal !== undefined ? f(p100 * cCal) : ""}</td>
        </tr>
      );
    }
  }

  return (
    <div className="FoodTable">
      <h3>Informações nutricionais</h3>
      <h4>Porção em gramas: <input
          className="portionSize"
          type='number'
          defaultValue={portionSize}
          onChange={v => setPortionSize(+v.target.value)}
        />
      </h4>
      <table>
        <thead>
          <td></td>
          <td>Total<br/>Tabelado</td>
          <td>
              Por {portionSize}g<br/>
              Tabelado
          </td>
          <td>KCal total<br/>Calculado</td>
          <td>KCal por {portionSize}g<br/>Calculado</td>
        </thead>
        <tbody>
          {main.map((k, i) => <NutrValue key={i} name={k} />)}
          <tr><td className="bottom-border"><br/></td></tr>
          {(Object.keys(picked) as (keyof FoodItemNumbers)[])
            .filter(k => !main.some(x => x === k))
            .map((k, i) => (<NutrValue key={i} name={k}/>))
          }
        </tbody>
      </table>
    </div>
  );
}

type SelectedFoodsProps = {
  foods: PickedFood[];
  setNewAmount: (i: number, amt: number) => void;
  delItem: (i: number) => void;
  clearAll: () => void;
};

function SelectedFoods({ foods, delItem, setNewAmount, clearAll }: SelectedFoodsProps) {
  if (foods.length === 0) return <></>;

  type SelectedFoodProps = {
    food: PickedFood;
    setAmount: (amt: number) => void;
    onDelete: () => void;
  };

  function SelectedFood({ food, onDelete, setAmount }: SelectedFoodProps) {
    const [newAmount, setNewAmount] = useState<number|null>(null);

    const toggleEditing = () => {
      if (newAmount !== null) {
        setAmount(newAmount);
        setNewAmount(null);
      } else {
        setNewAmount(food.amt);
      }
    };
    return (
      <tr>
        <td>{food.item.name}</td>
        {newAmount === null
          ? <td>{food.amt}</td>
          : (<td>
            <input
              type='number'
              onChange={e => setNewAmount(+e.target.value)}
            />
          </td>)
        }
        <td>
          <button onClick={toggleEditing}>✏️</button>
          <button onClick={onDelete}>❌</button>
        </td>
      </tr>
    );
  }

  const totalWeight = foods.reduce((t, i) => t + i.amt, 0.0);

  return (
    <>
    <table className="SelectedFoods">
      <thead>
        <tr>
          <th>Ingrediente</th>
          <th>Quantidade (gramas)</th>
        </tr>
      </thead>
      <tbody>
        {foods.map((f, i) => (
          <SelectedFood
            key={i}
            food={f}
            setAmount={amt => setNewAmount(i, amt)}
            onDelete={() => delItem(i)}
          />
        ))}
      </tbody>
      <tfoot>
        <td>Total</td>
        <td>{totalWeight}</td>
      </tfoot>
    </table>
    <button className="clear-all" onClick={clearAll}>Limpar tudo</button>
    </>
  )
}

type TableProps = {
  foods: PickedFood[];
  setFoods: React.Dispatch<React.SetStateAction<PickedFood[]>>;
};

export default function Table({ foods, setFoods }: TableProps) {
  return (
    <div className="Table">
      <div className="Table-editor">
        <FoodPrompt onSelect={picked => setFoods([picked, ...foods])} />
        <SelectedFoods
          foods={foods}
          delItem={del => setFoods(foods.filter((_, i) => i !== del)) }
          clearAll={() => setFoods([])}
          setNewAmount={(index, amount) => {
            const newFoods = [...foods];
            newFoods[index].amt = amount;
            setFoods(newFoods);
          }}
        />
      </div>
      {foods.length > 0
        ? <FoodTable pickedFoods={foods} />
        : null
      }
    </div>
  );
}
